import { getAnalytics, logEvent, setUserProperties } from "firebase/analytics";
import {
  getAppBuildVersion,
  getAppSessionUuid,
  getPlatform,
  getWebAppVersion,
} from "./config";
import { GA_LABEL3, POST_TYPES } from "./constants";
import { getProductType } from "./productUtils";

// Links every event of one search journey (search -> results -> impressions
// -> product click -> PDP/cart) via a shared search_id. sessionStorage so it
// survives SPA navigation; a new search overwrites the previous id.
const SEARCH_ID_KEY = "sourcing_search_id";
let activeSearchFlow = null;

const createSearchId = () => {
  let randomId = "";
  try {
    randomId = window.crypto?.randomUUID?.() || "";
  } catch (e) {
    // Fall through to the WebView-safe random value below.
  }
  return `${
    getAppSessionUuid() ||
    randomId ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }-${Date.now()}`;
};

export const startSearchFlow = (surface) => {
  // getAppSessionUuid() is empty on plain web; fall back to a UUID so
  // concurrent users can't collide on a bare timestamp.
  const searchId = createSearchId();
  activeSearchFlow = { id: searchId, surface };
  try {
    sessionStorage.setItem(SEARCH_ID_KEY, JSON.stringify(activeSearchFlow));
  } catch (e) {
    // In-memory flow still scopes events while this page remains loaded.
  }
  return searchId;
};

export const endSearchFlow = (surface) => {
  let storedFlow = activeSearchFlow;
  try {
    storedFlow =
      JSON.parse(sessionStorage.getItem(SEARCH_ID_KEY)) || activeSearchFlow;
  } catch (e) {
    // Fall back to memory when sessionStorage is unavailable.
  }
  if (!surface || storedFlow?.surface === surface) {
    activeSearchFlow = null;
    try {
      sessionStorage.removeItem(SEARCH_ID_KEY);
    } catch (e) {
      // Memory was cleared above.
    }
  }
};

export const getSearchId = (surface) => {
  try {
    const storedFlow = JSON.parse(sessionStorage.getItem(SEARCH_ID_KEY));
    if (storedFlow?.surface === surface) return storedFlow?.id || "";
  } catch (e) {
    // Fall back to memory when sessionStorage is unavailable.
  }
  return activeSearchFlow?.surface === surface
    ? activeSearchFlow?.id || ""
    : "";
};

export const triggerGa = ({
  category = "",
  action = "",
  label = "",
  label2 = "",
  label3 = "",
  page = "",
  screenName = "",
  eventValue = 0,
  custom = "",
  post_id = 0,
  collection_id = 0,
  product_id = 0,
  searchSurface = "",
  realEstate1 = "",
  realEstate2 = "",
  realEstate3 = "",
}) => {
  if (!localStorage.adminToken) {
    const searchId = searchSurface ? getSearchId(searchSurface) : "";
    const eventDetails = {
      event_category: category,
      event_label: label,
      event_location: window.location.pathname,
      event_label2: label2,
      event_label3: label3,
      page_title: page,
      screen_name: screenName,
      value: eventValue,
      custom_parameter: custom,
      post_id: post_id,
      collection_id: collection_id,
      product_id: product_id,
      platform: getPlatform(),
      app_build: getAppBuildVersion(),
      app_version: getWebAppVersion(),
      app_open_id: getAppSessionUuid(),
      ...(searchId ? { search_id: searchId } : {}),
      ...(realEstate1 ? { real_estate1: realEstate1 } : {}),
      ...(realEstate2 ? { real_estate2: realEstate2 } : {}),
      ...(realEstate3 ? { real_estate3: realEstate3 } : {}),
    };
    // window.gtag("event", action, eventDetails);  // removed for setting up GA with firebase analytics for web as well
    const analytics = getAnalytics();
    logEvent(analytics, action, eventDetails);
  }
};

export const triggerSlAuthResponse = ({
  category = "",
  action = "",
  platform = "", // ig/yt/fb/tg
  status = "", // success/fail
  failReason = "", // fail reason
  page = "",
  screenName = "",
}) => {
  if (!localStorage.adminToken) {
    triggerGa({
      category: category,
      action: action,
      label: platform,
      label2: status,
      label3: failReason,
      page: page,
      screenName: screenName,
    });
  }
};

export const setUserPropertiesGa = (property_params = {}) => {
  const analytics = getAnalytics();
  setUserProperties(analytics, {
    ...property_params,
    platform: getPlatform(),
    app_build: getAppBuildVersion(),
    app_version: getWebAppVersion(),
    app_open_id: getAppSessionUuid(),
  });
};

export const buildProductCustomParams = (product) =>
  JSON.stringify({
    scraping_status: product?.is_valid ? "scraped" : "unscraped",
    hidden_status: product?.is_alive ? "visible" : "hidden",
    product_type: getProductType(product),
    commission_type:
      !product?.customization_type ||
      product?.customization_type === "COMMISSIONABLE"
        ? "COMMISSIONABLE"
        : "NON-COMMISSIONABLE",
  });

export const getInstagramPostTypeLabel = (postType) => {
  switch (postType) {
    case POST_TYPES.INSTA_IMAGE:
      return GA_LABEL3.IG_POST;
    case POST_TYPES.INSTA_STORY:
      return GA_LABEL3.IG_STORY;
    case POST_TYPES.INSTA_REEL:
      return GA_LABEL3.IG_REEL;
    default:
      return "";
  }
};
